import { useCallback, useLayoutEffect, useState } from "react";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

/** Start on the actual surface mount, including delayed Radix portal mounts.
 * Two frames preserve the first painted state on Safari 16. The timer also
 * settles inactive desktop webviews whose animation frames are suspended.
 */
export function useTransitionsRecipeEntry() {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [enteredNode, setEnteredNode] = useState<HTMLElement | null>(null);
  const reducedMotion = useReducedMotion();
  const ref = useCallback((element: HTMLElement | null) => {
    // Composed refs can detach/reattach the same DOM node in one commit.
    // Reset only after React commits a real absence, not on that transient null.
    setNode(element);
  }, []);

  useLayoutEffect(() => {
    if (!node) {
      setEnteredNode(null);
      return;
    }
    if (reducedMotion) {
      setEnteredNode(node);
      return;
    }
    let nextFrame = 0;
    const enter = () => setEnteredNode(node);
    const firstFrame = requestAnimationFrame(() => {
      nextFrame = requestAnimationFrame(enter);
    });
    const timeout = window.setTimeout(enter, 80);
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(nextFrame);
      window.clearTimeout(timeout);
    };
  }, [node, reducedMotion]);

  return { ref, entered: !!node && enteredNode === node };
}
